/**
 * @file vehiculosPdfExporter.js
 * @description Exportador de vehículos a formato PDF con diseño profesional
 */

import {
  buildTableData,
  buildSummary,
  buildVehiculosTable,
  buildTotalBar,
  buildMarcaCards,
  buildFilename,
  pdfStylesBase,
  pdfDefaultStyle,
  formatDateTime,
  getDynamicLayoutSettings,
} from './pdfVehiculosCore';

const pdfMake = require('pdfmake/build/pdfmake.js');
const pdfFonts = require('pdfmake/build/vfs_fonts.js');

pdfMake.vfs = pdfFonts.pdfMake.vfs;

// -----------------------------------------------------
// 1) PDF de un solo sitio/subsitio (exportVehiculosPDF)
// -----------------------------------------------------
function buildDocDefinition(rows, visibleColumns, meta = {}) {
  const { headerRow, bodyRows } = buildTableData(rows, visibleColumns);
  const summary = buildSummary(rows);

  const titleParts = [];
  if (meta.sitio) titleParts.push(meta.sitio);
  if (meta.subSitio) titleParts.push(meta.subSitio);
  const title = titleParts.join(' : ') || 'Vehículos';

  const summaryContent = [
    buildTotalBar(`Total de autos: ${summary.total}`, 12),
    {
      text: 'Resumen por marca:',
      style: 'subheader',
      margin: [0, 4, 0, 8],
    },
    ...buildMarcaCards(summary),
  ];

  const { pageSize, finalFontSize, widths } = getDynamicLayoutSettings(visibleColumns);
  const table = buildVehiculosTable(headerRow, bodyRows, 16, { widths, fontSize: finalFontSize });

  const docDefinition = {
    pageSize: pageSize,
    pageOrientation: 'landscape',
    pageMargins: [10, 40, 10, 20],

    header: (currentPage, pageCount) => ({
      columns: [
        {
          stack: [
            {
              text: title,
              style: 'title',
              color: '#1F2937',
            },
            {
              text: `Generado: ${formatDateTime(new Date())}`,
              fontSize: 8,
              color: '#6B7280',
              margin: [0, 2, 0, 0],
            },
          ],
          margin: [15, 15, 0, 0],
        },
        {
          text: `Página ${currentPage} de ${pageCount}`,
          alignment: 'right',
          fontSize: 8,
          color: '#6B7280',
          margin: [0, 20, 15, 0],
        },
      ],
    }),

    content: [...summaryContent, table],
    styles: pdfStylesBase,
    defaultStyle: pdfDefaultStyle,
  };

  const filename = buildFilename(meta);
  return { docDefinition, filename };
}

export function exportVehiculosPDF(rows, visibleColumns, meta = {}) {
  try {
    const { docDefinition, filename } = buildDocDefinition(
      rows,
      visibleColumns,
      meta
    );
    pdfMake.createPdf(docDefinition).download(`${filename}.pdf`);
    console.log('PDF descargado:', `${filename}.pdf`);
  } catch (error) {
    console.error('Error al generar PDF:', error);
  }
}

// -----------------------------------------------------
// 2) PDF de un SITIO con todos sus SUBSITIOS
//    sections = [{ nombre, vehiculos: [...] }, ...]
// -----------------------------------------------------
export function exportSitioPDFConSubsitios(sections, visibleColumns, meta = {}) {
  try {
    if (!sections || !sections.length) {
      console.warn('exportSitioPDFConSubsitios: no hay secciones para exportar');
      return;
    }

    const totalGeneral = sections.reduce(
      (sum, sec) => sum + (sec.vehiculos?.length || 0),
      0
    );
    const sitioNombre = meta.sitio || 'Sitio';

    // Calcular layout dinámico una sola vez basado en colCount
    const { pageSize, finalFontSize, widths } = getDynamicLayoutSettings(visibleColumns);

    const content = [];

    // Resumen general del sitio
    content.push(
      buildTotalBar(`${sitioNombre} - Total de autos: ${totalGeneral}`, 13),
      {
        text: 'Resumen por subsitio:',
        style: 'subheader',
        margin: [0, 4, 0, 8],
      },
      {
        table: {
          headerRows: 1,
          widths: ['*', 'auto'],
          body: [
            [
              {
                text: 'Subsitio',
                style: 'tableHeader',
                fillColor: '#1F2937',
                color: '#FFFFFF',
                alignment: 'left',
                margin: [4, 6, 4, 6],
              },
              {
                text: 'Total de autos',
                style: 'tableHeader',
                fillColor: '#1F2937',
                color: '#FFFFFF',
                alignment: 'right',
                margin: [4, 6, 4, 6],
              },
            ],
            ...sections.map((sec) => [
              {
                text: sec.nombre,
                fontSize: 9,
                margin: [4, 4, 4, 4],
              },
              {
                text: String(sec.vehiculos?.length || 0),
                fontSize: 9,
                alignment: 'right',
                margin: [4, 4, 4, 4],
              },
            ]),
          ],
        },
        layout: {
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,
          hLineColor: () => '#E5E7EB',
          vLineColor: () => '#E5E7EB',
        },
        margin: [0, 0, 0, 16],
      }
    );

    // Detalle por subsitio
    sections.forEach((sec, index) => {
      const rows = sec.vehiculos || [];
      if (!rows.length) return;

      const { headerRow, bodyRows } = buildTableData(rows, visibleColumns);
      const summary = buildSummary(rows);

      if (index > 0) {
        content.push({ text: '', pageBreak: 'before' });
      }

      content.push(
        {
          text: `${sec.nombre} (${summary.total})`,
          style: 'subheader',
          margin: [0, 0, 0, 6],
        },
        buildTotalBar(`Total de autos en subsitio: ${summary.total}`, 11),
        {
          text: 'Resumen por marca:',
          style: 'subheader',
          margin: [0, 2, 0, 6],
        },
        ...buildMarcaCards(summary),
        ...buildMarcaCards(summary),
        buildVehiculosTable(headerRow, bodyRows, 8, { widths, fontSize: finalFontSize })
      );
    });

    const docDefinition = {
      pageSize: pageSize,
      pageOrientation: 'landscape',
      pageMargins: [10, 40, 10, 20],
      header: (currentPage, pageCount) => ({
        columns: [
          {
            stack: [
              {
                text: meta.sitio ? `Sitio: ${meta.sitio}` : 'Vehículos por sitio',
                style: 'title',
                color: '#1F2937',
              },
              {
                text: `Generado: ${formatDateTime(new Date())}`,
                fontSize: 8,
                color: '#6B7280',
                margin: [0, 2, 0, 0],
              },
            ],
            margin: [15, 15, 0, 0],
          },
          {
            text: `Página ${currentPage} de ${pageCount}`,
            alignment: 'right',
            fontSize: 8,
            color: '#6B7280',
            margin: [0, 20, 15, 0],
          },
        ],
      }),
      content,
      styles: pdfStylesBase,
      defaultStyle: pdfDefaultStyle,
    };

    const filename = buildFilename({
      sitio: meta.sitio,
      subSitio: 'todos_subsitios',
    });

    pdfMake.createPdf(docDefinition).download(`${filename}.pdf`);
    console.log('PDF (sitio con subsitios) descargado:', `${filename}.pdf`);
  } catch (error) {
    console.error('Error al generar PDF (sitio con subsitios):', error);
  }
}

// -----------------------------------------------------
// 3) PDF de un ESTADO → sitios y subsitios
// -----------------------------------------------------
export function exportEstadoPDFPorSitio(sitios, visibleColumns, meta = {}) {
  try {
    if (!sitios || !sitios.length) {
      console.warn('exportEstadoPDFPorSitio: no hay sitios para exportar');
      return;
    }

    const estadoNombre = meta.estado || 'Estado';

    // Calcular layout dinámico una sola vez basado en colCount
    const { pageSize, finalFontSize, widths } = getDynamicLayoutSettings(visibleColumns);

    const totalGeneral = sitios.reduce((sum, sitio) => {
      const secciones = sitio.secciones || [];
      const totalSitio = secciones.reduce(
        (acc, sec) => acc + (sec.vehiculos?.length || 0),
        0
      );
      return sum + totalSitio;
    }, 0);

    const content = [];

    // Resumen general del estado
    content.push(
      buildTotalBar(
        `Sitios del estado ${estadoNombre} - Total de autos: ${totalGeneral}`,
        13
      ),
      {
        text: 'Resumen por sitio:',
        style: 'subheader',
        margin: [0, 4, 0, 8],
      },
      {
        table: {
          headerRows: 1,
          widths: ['*', 'auto', 'auto'],
          body: [
            [
              {
                text: 'Sitio',
                style: 'tableHeader',
                fillColor: '#1F2937',
                color: '#FFFFFF',
                alignment: 'left',
                margin: [4, 6, 4, 6],
              },
              {
                text: 'Subsitios con vehículos',
                style: 'tableHeader',
                fillColor: '#1F2937',
                color: '#FFFFFF',
                alignment: 'center',
                margin: [4, 6, 4, 6],
              },
              {
                text: 'Total de autos',
                style: 'tableHeader',
                fillColor: '#1F2937',
                color: '#FFFFFF',
                alignment: 'right',
                margin: [4, 6, 4, 6],
              },
            ],
            ...sitios.map((s) => {
              const seccionesUtiles = (s.secciones || []).filter(
                (sec) => (sec.vehiculos?.length || 0) > 0
              );
              const totalSitio = seccionesUtiles.reduce(
                (acc, sec) => acc + (sec.vehiculos?.length || 0),
                0
              );
              return [
                {
                  text: s.sitio,
                  fontSize: 9,
                  margin: [4, 4, 4, 4],
                },
                {
                  text: String(seccionesUtiles.length),
                  fontSize: 9,
                  alignment: 'center',
                  margin: [4, 4, 4, 4],
                },
                {
                  text: String(totalSitio),
                  fontSize: 9,
                  alignment: 'right',
                  margin: [4, 4, 4, 4],
                },
              ];
            }),
          ],
        },
        layout: {
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,
          hLineColor: () => '#E5E7EB',
          vLineColor: () => '#E5E7EB',
        },
        margin: [0, 0, 0, 16],
      }
    );

    // Detalle por sitio y subsitio
    sitios.forEach((sitio, sitioIndex) => {
      const seccionesUtiles = (sitio.secciones || []).filter(
        (sec) => (sec.vehiculos?.length || 0) > 0
      );
      if (!seccionesUtiles.length) return;

      const totalSitio = seccionesUtiles.reduce(
        (acc, sec) => acc + (sec.vehiculos?.length || 0),
        0
      );

      content.push({
        text: '',
        pageBreak: sitioIndex === 0 ? undefined : 'before',
      });

      // Franja oscura del sitio
      content.push({
        table: {
          widths: ['*'],
          body: [[{
            text: `Sitio: ${sitio.sitio} - Total de autos: ${totalSitio}`,
            style: 'subheader',
            color: '#FFFFFF',
            margin: [8, 6, 8, 6],
          }]],
        },
        layout: 'noBorders',
        margin: [0, 0, 0, 8],
        fillColor: '#3B82F6',
      });

      // Mini-resumen por subsitio
      content.push(
        {
          text: 'Resumen por subsitio:',
          style: 'subheader',
          margin: [0, 2, 0, 6],
        },
        {
          table: {
            headerRows: 1,
            widths: ['*', 'auto'],
            body: [
              [
                {
                  text: 'Subsitio',
                  style: 'tableHeader',
                  fillColor: '#1F2937',
                  color: '#FFFFFF',
                  alignment: 'left',
                  margin: [4, 6, 4, 6],
                },
                {
                  text: 'Total de autos',
                  style: 'tableHeader',
                  fillColor: '#1F2937',
                  color: '#FFFFFF',
                  alignment: 'right',
                  margin: [4, 6, 4, 6],
                },
              ],
              ...seccionesUtiles.map((sec) => [
                {
                  text: sec.nombre,
                  fontSize: 9,
                  margin: [4, 4, 4, 4],
                },
                {
                  text: String(sec.vehiculos?.length || 0),
                  fontSize: 9,
                  alignment: 'right',
                  margin: [4, 4, 4, 4],
                },
              ]),
            ],
          },
          layout: {
            hLineWidth: () => 0.5,
            vLineWidth: () => 0.5,
            hLineColor: () => '#E5E7EB',
            vLineColor: () => '#E5E7EB',
          },
          margin: [0, 0, 0, 10],
        }
      );

      // Detalle por subsitio
      seccionesUtiles.forEach((sec, secIndex) => {
        const rows = sec.vehiculos || [];
        if (!rows.length) return;

        const { headerRow, bodyRows } = buildTableData(rows, visibleColumns);
        const summary = buildSummary(rows);

        content.push(
          {
            text: `Subsitio: ${sec.nombre} (${summary.total})`,
            style: 'subsitioTitle',
            margin: [0, secIndex === 0 ? 14 : 24, 0, 6],
          },
          {
            text: 'Resumen por marca:',
            style: 'subheader',
            margin: [0, 2, 0, 4],
          },
          ...buildMarcaCards(summary),
          buildVehiculosTable(headerRow, bodyRows, 6, { widths, fontSize: finalFontSize })
        );
      });
    });

    const docDefinition = {
      pageSize: pageSize,
      pageOrientation: 'landscape',
      pageMargins: [10, 40, 10, 20],
      header: (currentPage, pageCount) => ({
        columns: [
          {
            stack: [
              {
                text: `Sitios del estado ${estadoNombre}`,
                style: 'title',
                color: '#1F2937',
              },
              {
                text: `Generado: ${formatDateTime(new Date())}`,
                fontSize: 8,
                color: '#6B7280',
                margin: [0, 2, 0, 0],
              },
            ],
            margin: [15, 15, 0, 0],
          },
          {
            text: `Página ${currentPage} de ${pageCount}`,
            alignment: 'right',
            fontSize: 8,
            color: '#6B7280',
            margin: [0, 20, 15, 0],
          },
        ],
      }),
      content,
      styles: pdfStylesBase,
      defaultStyle: pdfDefaultStyle,
    };

    const filename = buildFilename({
      sitio: `estado_${estadoNombre}`,
      subSitio: 'todos_los_sitios',
    });

    pdfMake.createPdf(docDefinition).download(`${filename}.pdf`);
    console.log('PDF (estado → sitios/subsitios) descargado:', `${filename}.pdf`);
  } catch (error) {
    console.error('Error al generar PDF (estado por sitio):', error);
  }
}
